import * as Sentry from '@sentry/react'

// Sentry frontend wire-up. Initialized once at app boot from main.tsx.
//
// Gating:
//   - VITE_SENTRY_DSN absent → skip silently. Lets the app run anywhere
//     (local dev without an account, CI sandboxes, etc.) without
//     blowing up.
//   - DEV mode → skip unless VITE_SENTRY_ENABLE_IN_DEV=true. Avoids
//     burning the free-tier quota on local-dev test errors.
//
// Sample rate: errors at 100% (Sentry default), traces at 0% (per
// Phase A decision A2 — soft-launch volume doesn't justify perf-trace
// quota burn). PII boundary closed (sendDefaultPii=false) per A3 —
// user identification is workspace ID only, set elsewhere via
// Sentry.setUser(). No emails or auth tokens.

// Environment label for Sentry events. Hostname-based at runtime, NOT
// import.meta.env.DEV / MODE / process.env.VERCEL_ENV — all of those
// resolve to 'production' for both dev.orkestrapro.com and the future
// app.orkestrapro.com because Vercel labels the current single deployment
// target as "production" regardless of which subdomain it serves. Hostname
// is the only signal that discriminates the two.
//
// Maintenance trigger: when production promotes to app.orkestrapro.com
// (or whichever domain ends up canonical), update the production-host
// branch below. Same applies if the dev subdomain ever changes.
function detectEnvironment(): string {
  if (typeof window === 'undefined') return 'unknown'
  const host = window.location.hostname
  if (host === 'app.orkestrapro.com') return 'production'
  if (host === 'dev.orkestrapro.com') return 'development'
  if (host.endsWith('.vercel.app')) return 'preview'
  return 'local'
}

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  const enableInDev = import.meta.env.VITE_SENTRY_ENABLE_IN_DEV === 'true'

  if (!dsn) return
  if (import.meta.env.DEV && !enableInDev) return

  Sentry.init({
    dsn,
    environment: detectEnvironment(),
    tracesSampleRate: 0,
    sendDefaultPii: false,
  })
}

export { Sentry }
